/**
 * AppShell — layout wrapper for all authenticated + subscribed screens.
 * Rule 2: AppShell wraps all authenticated screens.
 * Rule 6: Scroll position resets to top on every route change.
 * Rule 7: Redirects to /onboarding if profile.grade_level is null.
 */
import { Outlet, useLocation, Navigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useAuth }                from '@/context/AuthContext'
import { BottomNav }              from '@/components/BottomNav'
import { LeaveGuardDialog }       from '@/components/LeaveGuardDialog'
import { SessionResumeBanner }    from '@/components/SessionResumeBanner'
import { OfflineBanner }          from '@/components/OfflineBanner'
import { OfflineSyncManager }     from '@/components/OfflineSyncManager'
import { ReviewCountProvider }    from '@/context/ReviewCountContext'
import { BOTTOM_NAV_ROUTES, LEAVE_GUARD_ROUTES, ROUTES } from '@/lib/routes'
import { slideUp }                from '@/lib/motion-variants'
import { findActiveSession }      from '@/lib/session-storage'
import { useReducedMotion }       from '@/hooks/useReducedMotion'
import { useAnnouncer }           from '@/hooks/useAnnouncer'

export function AppShell() {
  const { profile, user } = useAuth()
  const location          = useLocation()
  const reducedMotion     = useReducedMotion()
  useAnnouncer()

  // Rule 6 — reset scroll on every route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [location.pathname])

  // I1: session resumption — async IDB read, only checked on Home screen
  const isHome = location.pathname === ROUTES.HOME
  const [resumableSession, setResumableSession] =
    useState<{ session: import('@/lib/session-storage').ActiveSession } | null>(null)

  useEffect(() => {
    let cancelled = false
    // Always resolve asynchronously — avoids sync setState in effect (react-hooks/set-state-in-effect).
    const load = async () => {
      const result = (isHome && user) ? await findActiveSession(user.id) : null
      if (!cancelled) setResumableSession(result)
    }
    load()
    return () => { cancelled = true }
  }, [isHome, user])

  // Large-text accessibility — bumps root font-size 16px → 18px (112.5%)
  // when profile.large_text is true. Attribute on <html> so the CSS
  // [data-large-text="true"] selector in index.css applies globally.
  useEffect(() => {
    const root = document.documentElement
    if (profile?.large_text) {
      root.setAttribute('data-large-text', 'true')
    } else {
      root.removeAttribute('data-large-text')
    }
    return () => { root.removeAttribute('data-large-text') }
  }, [profile?.large_text])

  // Parent role guard — parents use /parent, never the student shell.
  // Defense-in-depth: AppRouter routes parents to /parent directly, but if a
  // parent somehow reaches a student route (e.g. manual URL entry), bounce them.
  if (profile?.role === 'parent') {
    return <Navigate to={ROUTES.PARENT} replace />
  }

  // Rule 7 — onboarding guard: redirect if grade_level not yet set.
  // The parent guard above already returned for any parent profile, so
  // reaching this point guarantees role = 'student' — no role check needed.
  if (
    profile != null &&
    profile.grade_level == null &&
    !profile.is_managed &&
    !location.pathname.startsWith(ROUTES.ONBOARDING) &&
    !location.pathname.startsWith('/auth')
  ) {
    return <Navigate to={ROUTES.ONBOARDING} replace />
  }

  const showBottomNav  = BOTTOM_NAV_ROUTES.includes(location.pathname as typeof BOTTOM_NAV_ROUTES[number])
  const showLeaveGuard = LEAVE_GUARD_ROUTES.includes(location.pathname as typeof LEAVE_GUARD_ROUTES[number])

  return (
    <ReviewCountProvider>
      <div className="page">
        {/* PWA: offline indicator + reconnect sync — Rule 5 */}
        <OfflineBanner />
        <OfflineSyncManager />

        {resumableSession != null && (
          <SessionResumeBanner session={resumableSession.session} />
        )}

        <motion.main
          id="main"
          variants={reducedMotion ? {} : slideUp}
          initial="hidden"
          animate="visible"
          className="page-content"
          tabIndex={-1}
        >
          <Outlet />
        </motion.main>

        {showBottomNav  && <BottomNav />}
        {showLeaveGuard && <LeaveGuardDialog />}
      </div>
    </ReviewCountProvider>
  )
}
